import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { Toggle } from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Language, LANGUAGES_CONFIG } from '@/config/languages';
import { useThemeActions, useThemeState } from '@/context/theme-provider';
import { changeLanguage } from '@/lib/i18n';
import { useSettingsStore } from '@/store/use-settings-store';

export default function TradingSettings() {
  const { t } = useTranslation();
  const {
    showPositionMarketPnL,
    hideInactiveAccounts,
    preserveMarketOrderFormOnCreate,
  } = useSettingsStore(
    useShallow((state) => ({
      showPositionMarketPnL: state.showPositionMarketPnL,
      preserveMarketOrderFormOnCreate: state.preserveMarketOrderFormOnCreate,
      hideInactiveAccounts: state.hideInactiveAccounts,
    })),
  );

  return (
    <div className="space-y-8 pb-32 sm:pb-10">
      <ThemeToggle />
      <LanguageSelect />
      <div className="flex items-center justify-between gap-6">
        <div className="space-y-1">
          <span>
            {t(
              showPositionMarketPnL
                ? 'positionsTable.marketPnL'
                : 'positionsTable.netPnL',
            )}
          </span>
          <p className="text-muted-foreground">
            {t(
              showPositionMarketPnL
                ? 'positionsTable.marketPnLTooltip'
                : 'positionsTable.netPnLTooltip',
            )}
          </p>
        </div>
        <Toggle
          className="data-[state=checked]:bg-primary"
          checked={showPositionMarketPnL}
          onCheckedChange={(checked) =>
            useSettingsStore.setState({
              showPositionMarketPnL: checked,
            })
          }
        />
      </div>
      <div className="flex items-center justify-between gap-6">
        <div className="space-y-1">
          <span>{t('settingsDropdown.preserveMarketOrderForm')}</span>
          <p className="text-muted-foreground">
            {t('settingsDropdown.preserveMarketOrderFormTooltip')}
          </p>
        </div>
        <Toggle
          className="data-[state=checked]:bg-primary"
          checked={preserveMarketOrderFormOnCreate}
          onCheckedChange={(checked) =>
            useSettingsStore.setState({
              preserveMarketOrderFormOnCreate: checked,
            })
          }
        />
      </div>
      <div className="flex items-center justify-between gap-6">
        <div className="space-y-1">
          <span>{t('settingsDropdown.hideInactiveAccounts')}</span>
          <p className="text-muted-foreground">
            Hide any accounts that are not currently able to trade
          </p>
        </div>
        <Toggle
          aria-label={t('settingsDropdown.hideInactiveAccounts')}
          checked={hideInactiveAccounts}
          className="data-[state=checked]:bg-primary"
          onCheckedChange={(checked) =>
            useSettingsStore.setState({
              hideInactiveAccounts: checked,
            })
          }
        />
      </div>
    </div>
  );
}

function ThemeToggle() {
  const { t } = useTranslation();
  const { isDark } = useThemeState();
  const { toggleTheme } = useThemeActions();
  return (
    <div className="flex items-center justify-between gap-6">
      <div className="space-y-1">
        <span>
          {isDark
            ? t('settingsDropdown.darkMode')
            : t('settingsDropdown.lightMode')}
        </span>
        <p className="text-muted-foreground">
          {t('settingsDropdown.themeDescription')}
        </p>
      </div>
      <Toggle
        checked={isDark}
        onCheckedChange={toggleTheme}
        aria-label={t('settingsDropdown.toggleDarkMode')}
        className="data-[state=checked]:bg-primary"
      />
    </div>
  );
}

function LanguageSelect() {
  const { i18n, t } = useTranslation();
  const language = i18n.language as Language;
  return (
    <div className="flex items-center justify-between gap-6">
      <div className="space-y-1">
        <span>{t('settingsDropdown.language')}</span>
        <p className="text-muted-foreground">
          {t('settingsDropdown.languageDescription')}
        </p>
      </div>
      <Select
        value={language}
        onValueChange={(v) => changeLanguage(v as Language)}
      >
        <SelectTrigger
          id="estFillPriceFilter"
          className="max-w-24 rounded-none bg-muted text-base"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="end">
          {LANGUAGES_CONFIG.map((lang) => (
            <SelectItem
              className="text-base"
              value={lang.value}
              key={lang.value}
            >
              {lang.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
